import { Box, Card, CardContent, Divider, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LocalLoginForm } from './LocalLoginForm';
import { MfaForm } from './MfaForm';
import { MfaSetupForm } from './MfaSetupForm';
import { MfaTypePicker } from './MfaTypePicker';
import { NewPasswordForm } from './NewPasswordForm';
import { RegionSwitcher } from './RegionSwitcher';
import { ResetPasswordForm } from './ResetPasswordForm';
import { SsoOptions } from './SsoOptions';
import { LoginResult } from './api';
import { useRegionRedirect } from './useRegionRedirect';

type TabValue = 'local' | 'sso';

type MfaState = Extract<LoginResult, { status: 'MFA_REQUIRED' }> & { email: string };
type NewPasswordState = { session: string; email: string };
type ForgotPasswordState = 'request' | { email: string };
type MfaSetupState = { session: string; email: string };
type SelectMfaState = Extract<LoginResult, { status: 'SELECT_MFA_TYPE' }> & { email: string };

export const LoginPage = () => {
  const { t } = useTranslation();
  // Before anything else is decided: a returning visitor goes back to the region they
  // chose last time. Pre-authentication routing, so it belongs ahead of any form state.
  useRegionRedirect();
  const [tab, setTab] = useState<TabValue>('local');
  const [mfaState, setMfaState] = useState<MfaState | null>(null);
  const [newPasswordState, setNewPasswordState] = useState<NewPasswordState | null>(null);
  const [forgotPasswordState, setForgotPasswordState] = useState<ForgotPasswordState | null>(null);
  const [mfaSetupState, setMfaSetupState] = useState<MfaSetupState | null>(null);
  const [selectMfaState, setSelectMfaState] = useState<SelectMfaState | null>(null);
  // Set only when the switcher is used. Sent with the sign-in request so the choice is
  // saved to the profile (CNA-1569); left undefined otherwise so a detected browser
  // locale never overwrites a deliberate profile setting.
  const [chosenLang, setChosenLang] = useState<string | undefined>();

  const handleMfaRequired = (state: MfaState) => {
    setNewPasswordState(null);
    setSelectMfaState(null);
    setMfaState(state);
  };
  const handleNewPasswordRequired = (state: NewPasswordState) => {
    setMfaState(null);
    setNewPasswordState(state);
  };
  const handleMfaSetupRequired = (state: MfaSetupState) => {
    setMfaState(null);
    setNewPasswordState(null);
    setMfaSetupState(state);
  };
  const handleSelectMfaType = (state: SelectMfaState) => {
    setMfaState(null);
    setNewPasswordState(null);
    setSelectMfaState(state);
  };
  const handleForgotPassword = () => {
    setMfaState(null);
    setNewPasswordState(null);
    setForgotPasswordState('request');
  };
  const handleCodeSent = (email: string) => setForgotPasswordState({ email });
  const handleBack = () => {
    setMfaState(null);
    setNewPasswordState(null);
    setForgotPasswordState(null);
    setMfaSetupState(null);
    setSelectMfaState(null);
  };

  const renderCardBody = () => {
    // Enrolment and method choice come before the code form: both are reached instead of
    // it, and MFA_SETUP in particular must never fall through to "enter your code".
    if (mfaSetupState) {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.mfaSetup.title')}
          </Typography>
          <MfaSetupForm
            session={mfaSetupState.session}
            email={mfaSetupState.email}
            onBack={handleBack}
            chosenLang={chosenLang}
          />
        </CardContent>
      );
    }
    if (selectMfaState) {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.selectMfa.title')}
          </Typography>
          <MfaTypePicker
            session={selectMfaState.session}
            email={selectMfaState.email}
            options={selectMfaState.options}
            onChallenge={handleMfaRequired}
            onBack={handleBack}
          />
        </CardContent>
      );
    }
    if (mfaState) {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.mfa.title')}
          </Typography>
          <MfaForm mfaState={mfaState} onBack={handleBack} chosenLang={chosenLang} />
        </CardContent>
      );
    }
    if (newPasswordState) {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.newPassword.title')}
          </Typography>
          <NewPasswordForm
            session={newPasswordState.session}
            email={newPasswordState.email}
            onMfaRequired={handleMfaRequired}
            onMfaSetupRequired={handleMfaSetupRequired}
            onSelectMfaType={handleSelectMfaType}
            onBack={handleBack}
            chosenLang={chosenLang}
          />
        </CardContent>
      );
    }
    if (forgotPasswordState === 'request') {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.forgotPassword.title')}
          </Typography>
          <ForgotPasswordForm onCodeSent={handleCodeSent} onBack={handleBack} />
        </CardContent>
      );
    }
    if (forgotPasswordState) {
      return (
        <CardContent sx={{ px: 3, py: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('login.resetPassword.title')}
          </Typography>
          <ResetPasswordForm email={forgotPasswordState.email} onBack={handleBack} />
        </CardContent>
      );
    }
    return (
      <>
        {/* Tab bar: Local login vs SSO */}
        <Tabs
          value={tab}
          onChange={(_, v: TabValue) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="local" label={t('login.tabs.local')} />
          <Tab value="sso" label={t('login.tabs.sso')} />
        </Tabs>

        <CardContent sx={{ px: 3, py: 3 }}>
          {tab === 'local' ? (
            <LocalLoginForm
              onMfaRequired={handleMfaRequired}
              onNewPasswordRequired={handleNewPasswordRequired}
              onMfaSetupRequired={handleMfaSetupRequired}
              onSelectMfaType={handleSelectMfaType}
              onForgotPassword={handleForgotPassword}
              chosenLang={chosenLang}
            />
          ) : (
            <SsoOptions />
          )}
        </CardContent>
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'primary.light',
        // CNA-1969: true vertical centring reads as sitting too low, so the card is nudged up
        // by padding the bottom more than the top rather than by an offset. The lift is half
        // the difference between the two, and this theme's spacing unit is 4px (theme.ts),
        // so 16px top against 64px bottom raises the block by 24px.
        pt: 4,
        pb: 16,
        px: 2,
      }}
    >
      {/* Region + language switchers — top of card. Region is CNA-1902/1906: a pre-auth
          routing decision, so it lives here rather than inside any authenticated screen. */}
      <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <RegionSwitcher />
        <LanguageSwitcher onChange={setChosenLang} />
      </Box>

      <Card elevation={3} sx={{ width: '100%', maxWidth: 440, borderRadius: 2 }}>
        {/* Logo + title */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 4,
            pb: 2,
            px: 3,
            gap: 1,
          }}
        >
          <Box component="img" src="/assets/images/logo-rec.png" alt="Cinareo" sx={{ height: 56, mb: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t('login.title')}
          </Typography>
        </Box>

        <Divider />

        {renderCardBody()}
      </Card>

      {/* Footer */}
      <Typography variant="caption" color="text.secondary" mt={3} sx={{ fontSize: '0.6875rem' }}>
        {t('footer.copyright', { year: new Date().getFullYear() })}
      </Typography>
    </Box>
  );
};
